import React from 'react';
import { EntryPointContainer, type EntryPoint, type EntryPointComponent } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import type { ErrorBoundaryFallbackComponent } from '@jira/platform__error-boundary/src/ErrorBoundary.tsx';
import { useHorizontalNavComponent } from '@jira/navigation-apps__horizontal-nav-selector/src/controllers/nav-component-controller/index.tsx';
import { NavErrorBoundary } from '@jira/navigation-apps__horizontal-nav-error-boundary/src/ui/js-error-boundary/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { HORIZONTAL_NAVIGATION } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { HorizontalNavSkeleton } from '@jira/navigation-apps__horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';

const NavErrorBoundaryFallback: ErrorBoundaryFallbackComponent = () =>
	// null = equivalent of "unmount"
	// We should do more graceful degradation.
	// Let's review this after dogfooding:
	// Tracked https://hello.jira.atlassian.cloud/browse/SEA-3059
	null;

const entrypointContainerProps = {};

export const HorizontalNavEntry = (): React.JSX.Element | null => {
	const HorizontalNavComponent = useHorizontalNavComponent();
	// Consume the horizontal nav entry point if defined, otherwise fallback to the old horizontal nav component
	const horizontalNavEntryPointReference =
		usePageLayoutEntryPoint<
			EntryPoint<
				EntryPointComponent<{}, {}, {}, { fallback: () => JSX.Element | undefined }>,
				{ fallback: () => JSX.Element | undefined }
			>
		>(HORIZONTAL_NAVIGATION);

	const horizontalNavFallback = () => {
		const Fallback =
			horizontalNavEntryPointReference?.extraProps?.fallback || HorizontalNavSkeleton;
		return (
			<>
				<ReportSSRFailOnUse
					pageId="navigation"
					fallbackId="horizontal-nav-through-entry-point"
					displayErrorInStaging
				/>
				<Fallback />
			</>
		);
	};

	if (horizontalNavEntryPointReference) {
		return (
			<>
				<UFOSegment name="horizontal-nav-through-entry-point">
					<JSErrorBoundary
						fallback="unmount"
						id="horizontal-nav-through-entry-point"
						packageName="page-container"
					>
						<EntryPointReferencePlaceholder
							name="horizontal-nav-through-entry-point"
							fallback={horizontalNavFallback()}
							entryPointReference={horizontalNavEntryPointReference}
						>
							<EntryPointContainer
								entryPointReference={horizontalNavEntryPointReference}
								props={entrypointContainerProps}
							/>
						</EntryPointReferencePlaceholder>
					</JSErrorBoundary>
				</UFOSegment>
			</>
		);
	}

	return (
		HorizontalNavComponent && (
			<NavErrorBoundary
				packageName="page-container"
				id="spa-horizontal-nav"
				fallback={NavErrorBoundaryFallback}
			>
				<HorizontalNavComponent />
			</NavErrorBoundary>
		)
	);
};
